// Number nicknames (Numbers PR 2): one line per number, "+1 (201) 285-8570 · Sales",
// with Rename / Add a name. Used by Settings → Your numbers and by the admin user page.
// The save function answers with an error text or null; the server applies the rules
// (24 characters, one name per account), the form checks the easy ones first.

import { useState, type FormEvent } from 'react';
import { formatPhone } from '../phone/number';
import { NICKNAME_MAX, nicknameOf, nicknameProblem, type NumberNames } from '../phone/number-names';

export function NumberNamesEditor({
  numbers,
  names,
  onSave,
  disabled = false,
  idPrefix = 'nn',
}: {
  numbers: readonly string[];
  names: NumberNames;
  /** save one name (null removes it); resolves to an error text, or null when saved */
  onSave: (number: string, nickname: string | null) => Promise<string | null>;
  disabled?: boolean;
  idPrefix?: string;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const open = (n: string) => {
    setEditing(n);
    setValue(nicknameOf(names, n) ?? '');
    setMsg(null);
  };

  const save = async (n: string, next: string | null) => {
    const problem = next === null ? null : nicknameProblem(next);
    if (problem) {
      setMsg({ tone: 'bad', text: problem });
      return;
    }
    setBusy(true);
    setMsg(null);
    const cleaned = next === null ? null : next.replace(/\s+/g, ' ').trim() || null;
    const err = await onSave(n, cleaned);
    setBusy(false);
    if (err) {
      setMsg({ tone: 'bad', text: err });
      return;
    }
    setEditing(null);
    setMsg({ tone: 'ok', text: cleaned ? `Saved: ${formatPhone(n)} is now “${cleaned}”.` : `Name removed from ${formatPhone(n)}.` });
  };

  const submit = (e: FormEvent, n: string) => {
    e.preventDefault();
    void save(n, value);
  };

  return (
    <div className="nn">
      <ul className="nn-list">
        {numbers.map((n) => {
          const name = nicknameOf(names, n);
          const inputId = `${idPrefix}-${n.replace(/\D/g, '')}`;
          return (
            <li key={n} className="nn-row">
              {editing === n ? (
                <form className="nn-form" onSubmit={(e) => submit(e, n)}>
                  <label className="nn-label" htmlFor={inputId}>
                    Name for {formatPhone(n)}
                  </label>
                  <div className="nn-edit">
                    <input
                      id={inputId}
                      className="nn-input"
                      value={value}
                      maxLength={NICKNAME_MAX + 8}
                      placeholder="e.g. Sales, Personal"
                      autoComplete="off"
                      autoFocus
                      disabled={busy}
                      onChange={(e) => setValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditing(null);
                        }
                      }}
                    />
                    <button className="btn btn-small" type="submit" disabled={busy}>
                      {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-small btn-quiet" type="button" disabled={busy} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                  <span className="nn-hint">
                    Up to {NICKNAME_MAX} characters. Only you and your admins see it; callers still see the number.
                    {name ? (
                      <>
                        {' '}
                        <button className="link-btn" type="button" disabled={busy} onClick={() => void save(n, null)}>
                          Remove the name
                        </button>
                      </>
                    ) : null}
                  </span>
                </form>
              ) : (
                <>
                  <span className="nn-text">
                    <span className="nn-number">{formatPhone(n)}</span>
                    {name ? <span className="nn-name">{name}</span> : <span className="nn-none">No name</span>}
                  </span>
                  <button className="btn btn-small" type="button" disabled={disabled || busy || editing !== null} onClick={() => open(n)}>
                    {name ? 'Rename' : 'Add a name'}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {msg && (
        <p className={`setting-msg setting-msg-${msg.tone}`} role={msg.tone === 'bad' ? 'alert' : 'status'}>
          {msg.text}
        </p>
      )}
    </div>
  );
}
